import { BadgeStatus } from '@/components/badge-order';
import DynamicImage from '@/components/commons/dynamic-image';
import LoadingCustomerOrderDetail from '@/components/sections/builder/elements/body/customer/customer_order/component/loading-detail';
import { CustomerOrderElementDataType } from '@/components/sections/builder/elements/body/customer/customer_order/type';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useExternalOrderDetail } from '@/hooks/api/storefront/orders';
import { cn, formatIDR, isEmpty } from '@/lib/utils';
import { StoreCustomerOrderDetailItem } from '@/types/http';
import { format } from 'date-fns';
import { Printer, Truck, Wallet, XIcon } from 'lucide-react';
import React from 'react';

type DetailDrawerProps = {
  content: CustomerOrderElementDataType;
  openModal?: boolean;
  setOpenModal?: (open: boolean) => void;
  data: { order_id: string };
  token?: string;
};

export function DetailDrawer({ content, openModal, setOpenModal, data, token }: DetailDrawerProps) {
  const orderId = data?.order_id || null;

  const { data: dataOrder, isLoading } = useExternalOrderDetail(
    orderId as string,
    token as string,
    {
      enabled: Boolean(openModal && orderId),
    }
  );

  const handleInvoice = () => {
    window.open(`/account/order/invoice/${dataOrder?.order_id}`, '_blank');
  };

  const handlePayment = () => {
    if (!dataOrder?.payment_link) return;
    window.open(dataOrder?.payment_link as string);
  };

  const handleTrackOrder = () => {
    if (!dataOrder?.awb_track_link) return;
    window.open(dataOrder?.awb_track_link as string);
  };

  return (
    <Drawer open={openModal} onOpenChange={setOpenModal} direction='right'>
      <DrawerContent className='fixed inset-y-0 right-0 z-[106] flex h-auto w-full flex-col bg-white p-4 sm:p-8 md:max-w-[450px] xl:max-w-[650px]'>
        <DrawerHeader className='flex items-center justify-between text-left'>
          <DrawerTitle className='text-xl xl:text-2xl'>Order Detail</DrawerTitle>
          <DrawerClose>
            <XIcon className='w-4 h-4' />
          </DrawerClose>
        </DrawerHeader>
        {isLoading ? (
          <LoadingCustomerOrderDetail />
        ) : (
          <>
            <ScrollArea className='w-full h-full px-4 pb-6'>
              <div className='flex flex-col w-full gap-3 mb-8 xl:mb-12'>
                <span className='text-lg xl:text-xl'>#{dataOrder?.order_no || '-'}</span>
                <div className='w-full'>
                  <BadgeStatus status={dataOrder?.status.toLowerCase() || 'unknown'} />
                </div>
              </div>
              {renderButton(
                dataOrder?.status.toLowerCase() || 'unknown',
                content,
                handlePayment,
                handleTrackOrder,
                handleInvoice
              )}

              <Separator className='my-4 border-gray-200 xl:my-6' />

              <div className='pb-3 mb-3 border-b border-gray-200 xl:mb-6 xl:pb-6'>
                <h2 className='mb-4 text-lg font-bold xl:mb-6 xl:text-xl'>Delivery</h2>
                <div className='grid w-full gap-6 mb-3 sm:grid-cols-2 xl:mb-6'>
                  <div className='flex flex-col text-sm xl:text-base'>
                    <h3 className='font-bold'>Date</h3>
                    <span>
                      {dataOrder?.created_at
                        ? format(new Date(dataOrder.created_at), 'dd MMMM yyyy')
                        : '-'}
                    </span>
                  </div>
                  <div className='text-sm xl:text-base'>
                    <h3 className='font-bold'>Address</h3>
                    <span>{dataOrder?.customer?.address || '-'}</span>
                  </div>
                </div>
                <div className='grid w-full gap-6 sm:grid-cols-2'>
                  <div className='text-sm xl:text-base'>
                    <h3 className='font-bold'>Courier</h3>
                    <span>{dataOrder?.shipping?.courier_name || '-'}</span>
                  </div>
                  <div className='text-sm xl:text-base'>
                    <h3 className='font-bold'>Payment Method</h3>
                    <span className='capitalize'>{dataOrder?.payment_method || '-'}</span>
                  </div>
                </div>
              </div>

              <div className='pb-3 mb-3 border-b border-gray-200 xl:mb-6 xl:pb-6'>
                <h2 className='mb-3 text-lg font-bold xl:mb-6 xl:text-xl'>
                  Item ({(dataOrder?.order_items && dataOrder?.order_items.length) || 0})
                </h2>
                <div className='flex flex-col w-full gap-y-4 md:gap-y-6 xl:gap-y-8'>
                  {dataOrder?.order_items &&
                    dataOrder?.order_items.length > 0 &&
                    dataOrder?.order_items?.map((item: StoreCustomerOrderDetailItem) => (
                      <div className='grid grid-cols-2 text-gray-700 gap-x-4' key={item.item_id}>
                        <div className='flex items-center gap-4 text-sm xl:text-base'>
                          <div className='relative flex-shrink-0 w-12 h-12 bg-gray-50 xl:h-16 xl:w-16'>
                            <DynamicImage
                              src={item.image as string}
                              alt={item.item_name as string}
                              width={64}
                              height={64}
                              className='object-contain rounded-md aspect-square md:rounded-lg'
                              sizes='100vw'
                            />
                          </div>
                          <div className='flex flex-col overflow-hidden'>
                            <h3 className='font-bold truncate '>{item?.product_name || '-'}</h3>
                            {item.product_name !== item.item_name && (
                              <span className='truncate'> {item.item_name}</span>
                            )}
                            <span className='text-sm'>x{item.qty}</span>
                          </div>
                        </div>
                        <div className='flex flex-col justify-between text-sm text-right xl:text-base'>
                          {(item?.discount_price as number) > 0 && (
                            <span className='justify-center font-bold whitespace-nowrap'>
                              {formatIDR(item.discount_price as number)} Rp 67000
                            </span>
                          )}
                          <span
                            className={cn(
                              'justify-center whitespace-nowrap',
                              item?.discount_price ? 'text-gray-400 line-through' : 'font-bold'
                            )}
                          >
                            {formatIDR(item.price)}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              <div className='mb-3 xl:mb-6 xl:pb-6'>
                <span className='text-sm font-semibold'>Notes:</span>
                <p className='text-base'>{dataOrder?.notes || '-'}</p>
              </div>

              {/* Order Summary */}
              <div className='pb-3 mb-3 border-b border-gray-200 xl:mb-6 xl:pb-6'>
                <h2 className='mb-5 text-lg font-bold xl:text-xl'>Order Summary</h2>
                <div className='space-y-2 text-sm xl:text-base'>
                  <div className='flex justify-between'>
                    <span>Subtotal</span>
                    <span>{formatIDR(dataOrder?.subtotal ?? 0)}</span>
                  </div>
                  {(dataOrder?.discount_amount as number) > 0 && (
                    <div className='flex justify-between'>
                      <span>Product discounts</span>
                      <span className='text-red-500'>
                        {formatIDR(dataOrder?.discount_amount as number)}
                      </span>
                    </div>
                  )}
                  <div className='flex justify-between'>
                    <span>Shipping cost</span>
                    <span>{formatIDR(dataOrder?.shipping_cost ?? 0)}</span>
                  </div>
                  {dataOrder?.is_include_insurance && (
                    <div className='flex justify-between'>
                      <span>Shipping insurance</span>
                      <span>{formatIDR(dataOrder?.insurance_cost ?? 0)}</span>
                    </div>
                  )}
                  {dataOrder?.is_cod && (
                    <div className='flex justify-between'>
                      <span>COD fee</span>
                      <span>{formatIDR(dataOrder?.cod_fee || 0)}</span>
                    </div>
                  )}
                  <div className='flex justify-between'>
                    <span>Platform fee</span>
                    <span>{formatIDR(dataOrder?.platform_fee ?? 0)}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Tax</span>
                    <span>{formatIDR(dataOrder?.total_tax ?? 0)}</span>
                  </div>
                </div>
              </div>
              <div className='flex items-center justify-between text-lg font-bold xl:text-xl'>
                <span>Total</span>
                <span>{formatIDR(dataOrder?.grand_total ?? 0)}</span>
              </div>
            </ScrollArea>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
}

const renderButton = (
  status: string,
  content: CustomerOrderElementDataType,
  handlePayment: () => void,
  handleTrackOrder: () => void,
  handleInvoice: () => void
) => {
  const divClass =
    'flex flex-col sm:justify-end items-start w-full gap-2 font-normal sm:flex-row sm:items-center sm:gap-1';
  const buttonClass = 'w-full gap-2 px-12 py-5 sm:w-full text-sm';

  switch (status) {
    case 'unpaid':
      return (
        <div className={divClass}>
          <Button type='button' variant='outline' className={buttonClass} onClick={handleInvoice}>
            <Printer className='w-4 h-4' />
            Invoice
          </Button>
          <Button
            type='button'
            className={cn(buttonClass, isEmpty(content?.colorScheme) && 'theme_button')}
            style={
              isEmpty(content?.colorScheme)
                ? {}
                : {
                    backgroundColor: content?.colorScheme?.btnSolidBackgroud,
                    color: content?.colorScheme?.btnSolidlabel,
                  }
            }
            onClick={handlePayment}
          >
            <Wallet className='w-4 h-4' />
            Pay
          </Button>
        </div>
      );
    case 'not_yet_shipped':
    case 'shipping':
    case 'shipped':
      return (
        <div className={divClass}>
          <Button type='button' variant='outline' className={buttonClass} onClick={handleInvoice}>
            <Printer className='w-4 h-4' />
            Invoice
          </Button>
          <Button
            type='button'
            className={cn(buttonClass, isEmpty(content?.colorScheme) && 'theme_button')}
            style={
              isEmpty(content?.colorScheme)
                ? {}
                : {
                    backgroundColor: content?.colorScheme?.btnSolidBackgroud,
                    color: content?.colorScheme?.btnSolidlabel,
                  }
            }
            onClick={handleTrackOrder}
          >
            <Truck className='w-4 h-4' />
            Track Order
          </Button>
        </div>
      );
    case 'paid':
    case 'ready_to_process':
    case 'processing':
    case 'cancel':
    case 'cancelled':
    case 'done':
    case 'expired':
      return (
        <div className={divClass}>
          <Button type='button' variant='outline' className={buttonClass} onClick={handleInvoice}>
            <Printer className='w-4 h-4' />
            Invoice
          </Button>
        </div>
      );
    default:
      return null;
  }
};
